import React, { useState, useMemo, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlanes } from '@/hooks/usePlanes';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import { CheckIcon, XMarkIcon, ArrowPathIcon, ArrowRightIcon, CheckCircleIcon, BoltIcon, ShieldCheckIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { useUser, useClerk } from '@clerk/clerk-react';
import toastService from '@/utils/toastService';
import { getPlanPricing, formatClp, formatUsd } from '@/utils/currencyUtils';
import { exchangeRateService } from '@/services/exchangeRateService';
import Modal from '@/components/common/Modal';
import { revvlyClerkModalTheme } from '@/config/clerkTheme';
// Animation styles will be handled with CSS classes

// CSS simple para ajustar footer del modal de Clerk en móvil
const globalClerkModalStyles = `
  /* Solo ajustar footer en móvil para que no lo cubra el sidebar */
  @media (max-width: 768px) {
    .cl-drawerFooter,
    .cl-internal-71m5fh,
    [class*="cl-internal-"][class*="footer"] {
      padding-bottom: 80px !important; /* Espacio para el sidebar móvil */
    }
  }
`;

// Componente de carga mejorado
const LoadingSpinner = ({ text = 'Procesando...' }) => (
  <div className="flex flex-col items-center justify-center py-8">
    <ArrowPathIcon className="w-12 h-12 text-blue-500 animate-spin mb-4" />
    <p className="text-gray-400 font-medium">{text}</p>
  </div>
);

// Componente de tarjeta de característica
const FeatureItem = ({ children, icon: Icon = CheckIcon, highlight = false }) => (
  <li className="flex items-start space-x-3">
    <Icon className={`flex-shrink-0 h-5 w-5 mt-0.5 ${highlight ? 'text-green-500' : 'text-blue-400'}`} />
    <span className={`text-sm ${highlight ? 'text-white font-medium' : 'text-gray-300'}`}>{children}</span>
  </li>
);

// Componente de tarjeta de plan mejorado
const PlanCard = ({ plan, current, onClick, selected, loading, exchangeRateInfo }) => {
  const isCurrent = current && plan.id === current.id;
  const priceInfo = plan ? getPlanPricing(plan, 'mensual') : null;
  const hasDiscount = priceInfo?.clp.original > priceInfo?.clp.current;

  return (
    <div 
      onClick={onClick}
      className={`relative group transition-all duration-300 cursor-pointer h-full ${
        selected 
          ? 'transform -translate-y-2' 
          : 'hover:transform hover:-translate-y-1'
      }`}
    >
      {/* Badge de plan actual */}
      {isCurrent && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap shadow-lg shadow-blue-500/20">
            Plan Actual
          </div>
        </div>
      )}
      
      {/* Badge de popular */}
      {plan?.isPopular && !isCurrent && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
          <div className="bg-gradient-to-r from-purple-600 to-blue-500 text-white text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-full whitespace-nowrap shadow-lg shadow-purple-500/20">
            Más Popular
          </div>
        </div>
      )}
      
      <div className={`h-full flex flex-col rounded-2xl overflow-hidden transition-all duration-300 ${
        selected
          ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-blue-500/50 shadow-2xl shadow-blue-500/10'
          : 'bg-gradient-to-br from-gray-800/80 to-gray-900/80 border border-gray-700/50 hover:border-blue-400/30 hover:shadow-lg hover:shadow-blue-500/5'
      }`}>
        <div className="p-6 pb-4 flex-1">
          {/* Header del plan */}
          <div className="mb-6 text-center">
            <h3 className={`text-2xl font-bold bg-gradient-to-r ${
              selected 
                ? 'from-blue-400 to-cyan-400' 
                : 'from-gray-200 to-gray-400'
            } bg-clip-text text-transparent`}>
              {plan?.name || 'Cargando...'}
            </h3>
            {plan?.description && (
              <p className="text-sm text-gray-400 mt-1">{plan.description}</p>
            )}
          </div>
          
          {/* Precio */}
          <div className="mb-8 text-center relative">
            <div className="flex items-baseline justify-center">
              <span className="text-4xl font-extrabold text-white">
                {priceInfo ? `$${priceInfo.clp.current.toLocaleString('es-CL')}` : '--'}
              </span>
              <span className="ml-2 text-gray-400">/mes</span>
            </div>
            
            {hasDiscount && (
              <div className="absolute -top-5 left-1/2 transform -translate-x-1/2">
                <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap shadow-md shadow-green-500/20">
                  Ahorra {Math.round((1 - priceInfo.clp.current / priceInfo.clp.original) * 100)}%
                </div>
              </div>
            )}
            
            {exchangeRateInfo?.rate && (
              <p className="text-sm text-gray-400 mt-2">
                ~ ${(priceInfo?.clp.current / exchangeRateInfo.rate).toFixed(2)} USD
              </p>
            )}
            
            {hasDiscount && (
              <p className="text-xs text-gray-500 line-through mt-1">
                ${priceInfo.clp.original.toLocaleString('es-CL')} valor normal
              </p>
            )}
          </div>
          
          {/* Características */}
          <ul className="space-y-3.5 mb-6">
            <FeatureItem highlight={true}>
              <span className="font-semibold">Hasta {plan?.max_usuarios || '∞'} usuarios</span>
            </FeatureItem>
            <FeatureItem>
              <span className="font-medium">{plan?.almacenamiento || 'Ilimitado'} GB</span> de almacenamiento
            </FeatureItem>
            <FeatureItem highlight={true}>
              Soporte <span className="font-semibold">prioritario</span>
            </FeatureItem>
            {plan?.caracteristicas?.map((feature, idx) => (
              <FeatureItem key={idx} icon={CheckIcon} highlight={idx < 2}>
                {feature}
              </FeatureItem>
            ))}
          </ul>
        </div>
        
        {/* Botón de acción */}
        <div className="px-6 pb-6 pt-2
          bg-gradient-to-t from-gray-900/80 via-gray-900/40 to-transparent">
          <button
            type="button"
            disabled={loading || isCurrent}
            className={`w-full py-3.5 px-6 rounded-xl font-semibold transition-all duration-300 transform hover:scale-[1.02] ${
              isCurrent
                ? 'bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-700/50'
                : selected
                ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:shadow-lg hover:shadow-blue-500/20 hover:from-blue-500 hover:to-cyan-500'
                : 'bg-gray-700/50 text-white hover:bg-gray-700/70 border border-gray-600/30 hover:border-blue-400/30'
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <ArrowPathIcon className="animate-spin h-5 w-5 mr-2" />
                Procesando...
              </div>
            ) : isCurrent ? (
              <div className="flex items-center justify-center">
                <CheckIcon className="h-5 w-5 mr-2 text-blue-400" />
                <span>Plan Actual</span>
              </div>
            ) : selected ? (
              <div className="flex items-center justify-center">
                <span>Seleccionado</span>
                <CheckIcon className="ml-2 h-5 w-5 text-white" />
              </div>
            ) : (
              'Seleccionar Plan'
            )}
          </button>
          
          {isCurrent && (
            <p className="text-xs text-center text-gray-500 mt-2">
              Tu plan actual
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Modal para procesar el pago de upgrade de plan
 */
const PaymentModal = ({ isOpen, onClose, targetPlanId }) => {
  // Hooks must be called at the top level, before any conditional returns
  const { planes, loading: planesLoading } = usePlanes();
  const { empresaActual } = useSimpleEmpresa();
  const { user } = useUser();
  const navigate = useNavigate();
  const { openUserProfile } = useClerk();
  
  // State declarations must be at the top level
  const [selectedPeriod, setSelectedPeriod] = useState('mensual');
  const [processing, setProcessing] = useState(false);
  const [exchangeRateInfo, setExchangeRateInfo] = useState({
    rate: 950,
    source: 'Cargando...',
    formatted: '950'
  });
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Memoize plan and price calculations
  const { currentPlan, targetPlan, prices } = useMemo(() => {
    const currentPlan = planes.find(plan => plan.id === empresaActual?.plan_id) || planes[0];
    const targetPlan = planes.find(plan => plan.id === targetPlanId);
    
    let prices = {
      current: { clp: 0, usd: 0 },
      target: { clp: 0, usd: 0 },
      difference: { clp: 0, usd: 0 },
      savings: { clp: 0, usd: 0 }
    };
    
    if (currentPlan && targetPlan) {
      const currentPricing = getPlanPricing(currentPlan, selectedPeriod);
      const targetPricing = getPlanPricing(targetPlan, selectedPeriod);
      
      prices = {
        current: currentPricing,
        target: targetPricing,
        difference: {
          clp: targetPricing.clp.current - currentPricing.clp.current,
          usd: targetPricing.usd.current - currentPricing.usd.current
        },
        savings: targetPricing.savings
      };
    }
    
    return { currentPlan, targetPlan, prices };
  }, [planes, empresaActual?.plan_id, targetPlanId, selectedPeriod]);

  // Cargar tasa de cambio cuando se abre el modal
  useEffect(() => {
    if (isOpen && targetPlan) {
      const loadExchangeRate = async () => {
        try {
          const info = await exchangeRateService.getExchangeRateInfo();
          setExchangeRateInfo(info);
        } catch (error) {
          console.error('❌ Error cargando tasa de cambio:', error);
          // Se mantiene el valor por defecto
          setExchangeRateInfo({
            rate: 950,
            source: 'Valor por defecto',
            formatted: '950',
            error: error.message
          });
        }
      };
      
      loadExchangeRate();
    }
  }, [isOpen, targetPlan]);


  // Add global Clerk modal styles to head
  useEffect(() => {
    if (!document.getElementById('payment-modal-styles')) {
      const style = document.createElement('style');
      style.id = 'payment-modal-styles';
      style.textContent = globalClerkModalStyles;
      document.head.appendChild(style);
    }
  }, []);

  // Load exchange rate
  useEffect(() => {
    const loadExchangeRate = async () => {
      try {
        const info = await exchangeRateService.getExchangeRateInfo();
        setExchangeRateInfo(info);
      } catch (err) {
        console.error('Error cargando tasa de cambio:', err);
        setExchangeRateInfo({
          rate: 950,
          source: 'Valor por defecto',
          formatted: '950',
          error: err.message
        });
      }
    };
    
    if (isOpen) {
      loadExchangeRate();
    }
  }, [isOpen]);

  // Función para cargar los planes
  const loadPlanes = async () => {
    setLoading(true);
    setError(null);
    try {
      // Aquí iría la lógica para cargar los planes
      // Por ahora, asumimos que los planes ya están cargados a través de usePlanes
    } catch (err) {
      setError('Error al cargar los planes');
      console.error('Error loading plans:', err);
    } finally {
      setLoading(false);
    }
  };

  // Cargar planes al montar el componente
  useEffect(() => {
    if (isOpen) {
      loadPlanes();
    }
  }, [isOpen]);


  // Función para manejar la selección de un plan
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setProcessingPlan(plan.id);
    // Aquí podrías agregar lógica adicional al seleccionar un plan
  };

  // Función para manejar la continuación del proceso
  const handleContinue = async () => {
    if (!selectedPlan) return;
    
    setProcessingPlan(selectedPlan.id);
    try {
      // Aquí iría la lógica para procesar el pago
      console.log('Procesando pago para plan:', selectedPlan.name);
      // Simulamos un tiempo de procesamiento
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (err) {
      console.error('Error al procesar el pago:', err);
      toastService.error('Error al procesar el pago');
    } finally {
      setProcessingPlan(null);
    }
  };

  // Handle close modal
  const handleClose = () => {
    onClose();
  };


  // Renderizar el modal de selección de planes
  if (!isOpen || !targetPlanId) {
    return null;
  }

  if (!targetPlan) {
    const planSelectionTitle = (
      <div className="flex items-center">
        <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-sky-400 bg-clip-text text-transparent">
          Mejora tu plan
        </span>
      </div>
    );

    const planSelectionFooter = (
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-gray-400 text-center md:text-left mb-4 md:mb-0">
            ¿Necesitas ayuda para elegir? <button type="button" className="text-blue-400 hover:text-blue-300 underline bg-transparent border-none cursor-pointer">Contáctanos</button>
          </p>
          <div className="flex space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white"
            >
              Cancelar
            </button>
            <button
              onClick={handleContinue}
              disabled={!selectedPlan || processingPlan}
              className={`px-6 py-2 text-sm font-medium rounded-md transition-colors ${
                selectedPlan
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-500/20'
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
            >
              {processingPlan ? (
                <span className="flex items-center">
                  <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Procesando...
                </span>
              ) : (
                <span className="flex items-center">
                  Continuar <ArrowRightIcon className="ml-2 h-4 w-4" />
                </span>
              )}
            </button>
          </div>
        </div>
        <p className="text-xs text-center text-gray-500">
          Al continuar, aceptas nuestros <button type="button" className="text-blue-400 hover:underline bg-transparent border-none cursor-pointer">Términos de Servicio</button> y la <button type="button" className="text-blue-400 hover:underline bg-transparent border-none cursor-pointer">Política de Privacidad</button>.
        </p>
      </div>
    );

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={planSelectionTitle}
        size="6xl"
        footer={planSelectionFooter}
      >
        {/* Descripción */}
        <div className="mb-6">
          <p className="text-gray-300 text-sm md:text-base">
            Selecciona el plan que mejor se adapte a las necesidades de tu taller
          </p>
        </div>
        
        {/* Toggle de facturación */}
        <div className="mb-6 flex justify-center">
          <div className="inline-flex items-center bg-gray-800/40 border border-gray-600 rounded-xl p-1 shadow-inner">
            <button
              onClick={() => setSelectedPeriod('mensual')}
              className={`relative px-6 py-2.5 text-sm font-medium rounded-xl transition-all duration-300 transform hover:scale-105 overflow-hidden group ${
                selectedPeriod === 'mensual'
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg shadow-blue-500/20'
                  : 'text-gray-300 hover:text-white bg-gray-800/30 hover:bg-gray-700/50 border border-gray-700/50'
              }`}
            >
              <span className="relative z-10 flex items-center">
                <span className={`mr-2 transition-transform duration-200 ${selectedPeriod === 'mensual' ? 'scale-100' : 'scale-0'}`}>✓</span>
                Facturación Mensual
              </span>
              {selectedPeriod === 'mensual' && (
                <span className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              )}
            </button>
            <div className="relative mx-2 flex items-center">
              <span className="px-2 py-1 text-xs font-bold bg-gray-800/80 text-blue-300 rounded-full shadow-inner border border-gray-700/50">
                -20%
              </span>
            </div>
            <button
              onClick={() => setSelectedPeriod('anual')}
              className={`relative px-6 py-2.5 text-sm font-medium rounded-xl transition-all duration-300 transform hover:scale-105 overflow-hidden group ${
                selectedPeriod === 'anual'
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/20'
                  : 'text-gray-300 hover:text-white bg-gray-800/30 hover:bg-gray-700/50 border border-gray-700/50'
              }`}
            >
              <span className="relative z-10 flex items-center">
                <span className={`mr-2 transition-transform duration-200 ${selectedPeriod === 'anual' ? 'scale-100' : 'scale-0'}`}>✓</span>
                Facturación Anual
              </span>
              {selectedPeriod === 'anual' && (
                <span className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              )}
            </button>
          </div>
        </div>
        
        {/* Contenido principal */}
        {loading ? (
          <LoadingSpinner text="Cargando planes disponibles..." />
        ) : error ? (
          <div className="text-center py-12">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-900/50 text-red-400 mb-4">
              <XMarkIcon className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-medium text-white mb-1">Error al cargar los planes</h3>
            <p className="text-gray-400 mb-6">No se pudieron cargar los planes. Por favor, verifica tu conexión e inténtalo de nuevo.</p>
            <button
              onClick={loadPlanes}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ArrowPathIcon className="-ml-1 mr-2 h-4 w-4" />
              Reintentar
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {planes?.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                current={currentPlan}
                onClick={() => handlePlanSelect(plan)}
                selected={selectedPlan?.id === plan.id}
                loading={processingPlan === plan.id}
                exchangeRateInfo={exchangeRateInfo}
              />
            ))}
          </div>
        )}
        
        {/* Características adicionales */}
        {!loading && !error && (
          <div className="mt-12 bg-gray-800/30 border border-gray-600 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Todas las características incluidas</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: ShieldCheckIcon, title: 'Seguridad', desc: 'Datos encriptados y respaldos automáticos' },
                { icon: BoltIcon, title: 'Rendimiento', desc: 'Alta disponibilidad y tiempos de carga rápidos' },
                { icon: CurrencyDollarIcon, title: 'Sin sorpresas', desc: 'Precios claros sin cargos ocultos' },
                { icon: CheckCircleIcon, title: 'Soporte', desc: 'Atención prioritaria 24/7' }
              ].map((feature, idx) => (
                <div key={idx} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <feature.icon className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-white">{feature.title}</h4>
                    <p className="text-xs text-gray-400 mt-1">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    );
  }

  const handleUpgradeToClerk = async () => {
    if (!user) {
      toastService.error('Usuario no autenticado');
      return;
    }

    setProcessing(true);
    
    try {
      // Obtener el plan ID de Clerk basado en el target plan y período
      const clerkPlanId = getClerkPlanId(targetPlan.name, selectedPeriod);
      
      console.log('🔄 Iniciando suscripción a plan:', clerkPlanId);
      
      // Intentar usar la API de Clerk para suscripción directa
      if (window.Clerk && window.Clerk.billing) {
        await window.Clerk.billing.subscribeToPlan(clerkPlanId);
        toastService.success(`¡Suscripción a ${targetPlan.name} exitosa!`);
        handleClose();
      } else {
        // Fallback: cerrar modal y mostrar PricingTable en página completa
        console.log('📋 API directa no disponible, redirigiendo a pricing');
        handleClose();
        navigate('/pricing');
        toastService.info('Elige tu plan y completa la suscripción');
      }
    } catch (error) {
      console.error('❌ Error iniciando suscripción:', error);
      console.log('📋 Error en suscripción directa, redirigiendo a pricing');
      handleClose();
      navigate('/pricing');
      toastService.info('Elige tu plan y completa la suscripción');
    } finally {
      setProcessing(false);
    }
  };

  const getClerkPlanId = (planName, period) => {
    // Mapear nombres de planes locales a IDs de Clerk
    // Estos IDs deben coincidir exactamente con los configurados en Clerk Dashboard
    const planMapping = {
      'Bronze': period === 'mensual' ? 'plan_bronze_monthly' : 'plan_bronze_annual',
      'Silver': period === 'mensual' ? 'plan_silver_monthly' : 'plan_silver_annual', 
      'Gold': period === 'mensual' ? 'plan_gold_monthly' : 'plan_gold_annual'
    };
    
    const clerkPlanId = planMapping[planName];
    
    
    return clerkPlanId || 'plan_bronze_monthly';
  };

  const paymentTitle = (
    <div className="flex flex-col">
      <span className="text-xl font-bold">
        Actualizar Plan
      </span>
      <span className="text-sm text-gray-400 mt-1">
        De {currentPlan.name} a {targetPlan.name}
      </span>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={paymentTitle}
      size="2xl"
    >

      <div className="flex flex-col lg:grid lg:grid-cols-[2fr_1fr] lg:items-start gap-6">
        {/* Sistema de pago - now on the left */}
        <div className="space-y-4">
          {/* Selector de periodo */}
          <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
            <h3 className="text-base font-semibold text-white mb-3">Periodo de facturación</h3>
            <div className="bg-gray-700 rounded-lg p-1 flex">
              <button
                type="button"
                onClick={() => setSelectedPeriod('mensual')}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedPeriod === 'mensual'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Mensual
              </button>
              <button
                type="button"
                onClick={() => setSelectedPeriod('anual')}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedPeriod === 'anual'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Anual
                <span className="ml-1 text-xs bg-green-500 text-white px-1 rounded">
                  -20%
                </span>
              </button>
            </div>
          </div>

          {/* Información del nuevo plan */}
          <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
            <div className="text-center">
              <h3 className="text-xl font-bold text-white mb-2">Plan {targetPlan.name}</h3>
              <div className="mb-1">
                <div className="text-3xl font-bold text-green-400">
                  {formatClp(prices.target.clp.current)} 
                </div>
                /{selectedPeriod === 'anual' ? 'año' : 'mes'}
                <div className="text-lg text-gray-300 flex items-center justify-center gap-2">
                  {formatUsd(prices.target.usd.current, true)}
                  <span className="text-xs text-gray-500">
                    (1 USD = ${exchangeRateInfo.formatted} CLP)
                  </span>
                </div>
              </div>
              <div className="text-gray-400 text-sm mb-4">
                {selectedPeriod === 'anual' && prices.savings.clp > 0 && (
                  <div className="mt-1">
                    <span className="text-green-400 font-medium">
                      Ahorra {formatClp(prices.savings.clp)} 
                    </span>
                    <span className="text-gray-400 text-xs ml-1">
                      ({formatUsd(prices.savings.usd, true)}) al año
                    </span>
                  </div>
                )}
              </div>
              <p className="text-gray-300 text-sm mb-4">{targetPlan.description}</p>
              
              {/* Subscribe button in mobile - shown only on small screens */}
              <div className="block lg:hidden space-y-3">
                <button
                  onClick={handleUpgradeToClerk}
                  disabled={processing}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  {processing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Procesando...</span>
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <span>Suscribirse a Plan {targetPlan.name}</span>
                    </>
                  )}
                </button>
                
                {/* Gestionar plan actual button for mobile */}
                <div className="pt-4 border-t border-gray-700">
                  <button
                    onClick={() => {
                      openUserProfile({
                        routing: 'modal',
                        appearance: revvlyClerkModalTheme
                      });
                      handleClose();
                    }}
                    className="w-full text-sm bg-gray-700/50 text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200 py-2 px-4 rounded-lg border border-gray-600/50 hover:border-gray-500"
                  >
                    ¿Ya tienes suscripción? Gestionar plan actual →
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sistema de pago - Hidden on mobile */}
          <div className="hidden lg:block">
            {/* Botón principal para suscribirse con Clerk */}
            <div className="bg-gray-800 border border-gray-600 rounded-lg p-6">
              {/* Subscribe button - shown only on desktop */}
              <div className="text-center mb-4">
                <div className="hidden lg:block">
                  <button
                    onClick={handleUpgradeToClerk}
                    disabled={processing}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    {processing ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Procesando...</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        <span>Suscribirse a Plan {targetPlan.name}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-700 hidden lg:block">
                <button
                  onClick={() => {
                    openUserProfile({
                      routing: 'modal',
                      appearance: revvlyClerkModalTheme
                    });
                    handleClose();
                  }}
                  className="w-full text-sm bg-gray-700/50 text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200 py-2 px-4 rounded-lg border border-gray-600/50 hover:border-gray-500"
                >
                  ¿Ya tienes suscripción? Gestionar plan actual →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right column - Features and Cancel button */}
        <div className="space-y-4">
          {/* Título de beneficios */}
          <h3 className="text-base font-semibold text-white mb-3">Lo que incluye</h3>
          
          {/* Beneficios del nuevo plan */}
          <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
            {/* Mostrar beneficios en una sola columna para desktop */}
            {targetPlan.beneficios && targetPlan.beneficios.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-3">
                {targetPlan.beneficios
                  .filter(beneficio => beneficio.incluido === true)
                  .map((beneficio, index) => (
                    <div key={index} className="flex items-center text-green-400">
                      <CheckIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span className="text-sm">{beneficio.titulo}</span>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-3">
                {/* Fallback a features si no hay beneficios */}
                {targetPlan.features && targetPlan.features.length > 0 ? (
                  targetPlan.features.map((feature, index) => (
                    <div key={index} className="flex items-center text-green-400">
                      <CheckIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-gray-400 text-sm">
                    No hay beneficios configurados para este plan
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Info section - Above cancel button */}
          <div className="text-xs text-gray-400">
            <div className="grid grid-cols-1 gap-y-1">
              <div>✓ Facturación automática {selectedPeriod}</div>
              <div>✓ Cancela cuando quieras</div>
              <div>✓ Acepta tarjetas de crédito</div>
              <div>✓ Transacciones 100% seguras con Clerk</div>
            </div>
          </div>

          {/* Botón de cancelar - Below info section */}
          <button
            type="button"
            onClick={handleClose}
            className="w-full px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors text-sm"
            disabled={processing}
          >
            Cancelar
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Versión memoizada con comparación inteligente
const MemoizedPaymentModal = memo(PaymentModal, (prevProps, nextProps) => {
  const hasChanged = prevProps.isOpen !== nextProps.isOpen || prevProps.targetPlanId !== nextProps.targetPlanId;
  
  // Solo re-renderizar si hay cambios reales
  return !hasChanged; // true = skip re-render, false = allow re-render
});

export default MemoizedPaymentModal;